<template>
  <div class="tags-columns rbz-columns" :class="{ 'faded-segment': disabled }">
    <template v-for="(column, index) in columns" :key="column.title">
      <div class="vertical-divider">
        <layout-divider-line color="light-gray" orientation="vertical" />
      </div>
      <div :class="['tags-column', 'rbz-column', column.classes]">
        <div class="tags-column-title">
          {{ column.title }}
        </div>
        <layout-divider-line color="light-gray" />
        <div class="lists-columns rbz-columns">
          <div
            v-for="(list, index) in column.lists"
            :key="list.title"
            :class="['lists-column', 'rbz-column', list.classes]"
          >
            <form-slider-toggle
              v-if="showSliderToggle"
              :is-toggled="hasMoreThanOneTag(list.tags)"
              :relation="list.relation"
              :disabled="readOnly"
              @update:selector="(newRelation) => updateTagsRelation(list.fieldName, newRelation)"
            />
            <tags-selection
              :list-header-divider="index < column.lists.length - 1"
              :list-title="list.title"
              :list-type="list.listType ?? listType"
              :modes="['list', 'dropdown']"
              :read-only="readOnly"
              :tag-tooltip="list.tagTooltip"
              :tag-variant="list.tagVariant"
              :value="list.tags"
              current-mode="list"
              :disabled="disabled"
              @update:value="tagsChanged(list.fieldName, $event as string[])"
            />
          </div>
        </div>
      </div>
      <div v-if="index === columns.length - 1" class="vertical-divider">
        <layout-divider-line color="light-gray" orientation="vertical" />
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import TagsSelection from '@/components/TagsSelection.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { TagsSelectionTableColumn, Relation } from '@/types';
import { ListType } from '@/store/TagsStore';
import FormSliderToggle from '@/components/FormSliderToggle.vue';

interface TagsSelectionTable {
  columns: TagsSelectionTableColumn[];
  listType?: ListType;
  readOnly?: boolean;
  showSliderToggle?: boolean;
  disabled?: boolean;
}

withDefaults(defineProps<TagsSelectionTable>(), {
  showSliderToggle: false,
});

interface OptionEmit {
  (e: 'update:tags', { fieldName, tags }: { fieldName: string; tags: string[] }): void;

  (
    e: 'update:tags-relation',
    { fieldName, relation }: { fieldName: string; relation: Relation }
  ): void;
}

const emit = defineEmits<OptionEmit>();

const tagsChanged = (fieldName: string, newTagsList: string[]) => {
  emit('update:tags', {
    fieldName: fieldName,
    tags: newTagsList,
  });
};

const updateTagsRelation = (fieldName: string, relation: Relation) => {
  emit('update:tags-relation', {
    fieldName: fieldName,
    relation: relation,
  });
};

const hasMoreThanOneTag = (tags: string[]) => {
  return tags && tags.length > 1;
};
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.tags-columns:not(:first-child) {
  margin-top: 32px;
}

.tags-columns.rbz-columns,
.lists-columns.rbz-columns {
  gap: 0;
}

.tags-column-title {
  color: map.get(colors.$colors, 'bright-gray');
  margin: 8px 0;
  text-align: center;
}

.vertical-divider {
  position: relative;
}

.faded-segment {
  opacity: 0.3;
}
</style>
